// Working copy of a saved view: filter, sort, columns and grouping change locally first; «Сохранить вид» writes them. The view's own
// menu (R-U2): rename, duplicate, personal or shared, remove — each a server command shown at once (optimistic).

import { useCallback, useEffect, useMemo, useState } from 'react';
import { uuidv7, type Filter, type Id, type MetaIndex, type SortKey, type ViewColumn, type ViewDef } from '@confdeck/core-shared';
import { useMeta, useSync } from '../sync/react.tsx';
import { useSetting } from '../settings/use-setting.ts';

export interface ViewDraft { filter?: Filter | null; sort?: SortKey[]; columns?: ViewColumn[]; groupBy?: ViewDef['groupBy']; layout?: ViewDef['layout']; summary?: ViewDef['summary']; gantt?: ViewDef['gantt']; calendar?: ViewDef['calendar']; drill?: Drill | null }

/** One condition of a drill-down, as the person reads it: «Стадия: Принят». */
export interface DrillChip { label: string; value: string }
/**
 * A table opened from a number of the summary (В14): the draft it came from, to return to, and the conditions shown as chips.
 * Chips go away once the person changes the filter by hand; the way back stays.
 */
export interface Drill { from: ViewDraft; chips: DrillChip[] }

const withoutDrill = ({ drill: _drill, ...rest }: ViewDraft): ViewDraft => rest;

const DEFAULT_WIDTH: Record<string, number> = { text: 220, longtext: 260, relation: 220, taxonomy: 180, user: 180, email: 220, url: 200, date: 130, datetime: 160, money: 140, number: 110, checkbox: 90 };

/**
 * The columns of a view that has none of its own: a summary view shown as a table (its number was clicked). The type's first
 * table view with columns lends them; without one every field shows, as the export does (core/server/src/export.ts).
 */
export function fallbackColumns(meta: MetaIndex, typeId: Id): ViewColumn[] {
  const lender = meta.views(typeId).find((v) => !v.archivedAt && v.layout === 'table' && v.columns.some((c) => !c.hidden));
  if (lender) return lender.columns;
  const title = meta.type(typeId)?.titleField;
  const fields = meta.fields(typeId).filter((f) => !f.archivedAt).sort((a, b) => Number(b.key === title) - Number(a.key === title));
  return fields.map((f) => ({ field: f.key, width: f.key === title ? 280 : DEFAULT_WIDTH[f.fieldType] ?? 160 }));
}

const key = (viewId: Id) => `confdeck:view-draft:${viewId}`;
function readDraft(viewId: Id): ViewDraft {
  try { return JSON.parse(localStorage.getItem(key(viewId)) ?? '{}') as ViewDraft; } catch { return {}; }
}
function writeDraft(viewId: Id, d: ViewDraft) {
  try {
    if (Object.keys(d).length) localStorage.setItem(key(viewId), JSON.stringify(d)); else localStorage.removeItem(key(viewId));
  } catch { /* drafts are a convenience; without storage they live until reload */ }
}

/**
 * Drafts of all views live in the person's setting «ui.viewDrafts» (L11): another computer of the person opens them as left.
 * The browser keeps a copy for the first paint; writes to the server are gathered for a second.
 */
let serverDrafts: Record<string, ViewDraft> = {};
let flush: ReturnType<typeof setTimeout> | null = null;
function saveServer(client: ReturnType<typeof useSync>, viewId: Id, d: ViewDraft) {
  const me = client.header?.userId;
  if (!me) return;
  const next = { ...serverDrafts };
  if (Object.keys(d).length) next[viewId] = d; else delete next[viewId];
  serverDrafts = next;
  if (flush) clearTimeout(flush);
  flush = setTimeout(() => { void client.run('setting.set', { key: 'ui.viewDrafts', scope: 'user', targetId: me, value: JSON.stringify(serverDrafts) }); }, 1000);
}

export function useViewState(view: ViewDef | undefined) {
  const client = useSync();
  const meta = useMeta();
  const saved = useSetting('ui.viewDrafts');
  useEffect(() => { try { serverDrafts = JSON.parse(typeof saved === 'string' && saved ? saved : '{}') as Record<string, ViewDraft>; } catch { serverDrafts = {}; } }, [saved]);
  const initial = (id: Id) => { const local = readDraft(id); return Object.keys(local).length ? local : serverDrafts[id] ?? {}; };
  const [draft, setDraftState] = useState<ViewDraft>(() => (view ? initial(view.id) : {}));
  useEffect(() => { setDraftState(view ? initial(view.id) : {}); }, [view?.id, saved]);
  /** One place that keeps a draft: the state, the browser and the server. */
  const keep = useCallback((viewId: Id, d: ViewDraft) => { writeDraft(viewId, d); saveServer(client, viewId, d); }, [client]);
  const setDraft = setDraftState;

  const update = useCallback((patch: ViewDraft) => {
    if (!view) return;
    setDraftState((d) => {
      const next = { ...d, ...patch };
      // A filter changed by hand no longer matches the drill's chips; another layout ends the drill-down.
      if (d.drill && patch.layout && patch.layout !== 'table') delete next.drill;
      else if (d.drill && patch.filter !== undefined && !patch.drill) next.drill = { ...d.drill, chips: [] };
      keep(view.id, next);
      return next;
    });
  }, [view?.id, keep]);

  /** Opens the table of a summary cell: the current draft is kept to come back to. */
  const drill = useCallback((patch: ViewDraft, chips: DrillChip[]) => {
    if (!view) return;
    setDraftState((d) => { const { drill: _old, ...from } = d; const next = { ...from, ...patch, drill: { from, chips } }; keep(view.id, next); return next; });
  }, [view?.id, keep]);

  /** Back from a drill-down to the summary exactly as it was. */
  const back = useCallback(() => {
    if (!view) return;
    setDraftState((d) => { const next = d.drill?.from ?? d; keep(view.id, next); return next; });
  }, [view?.id, keep]);

  const effective: ViewDef | undefined = useMemo(() => {
    if (!view) return undefined;
    const v = { ...view, ...withoutDrill(draft) } as ViewDef;
    // Only a table borrows columns: a board, gantt or summary without its own keeps none (review P3.2).
    return v.columns.length || v.layout !== 'table' ? v : { ...v, columns: fallbackColumns(meta, view.typeId) };
  }, [view, draft, meta, meta.all.size]);
  const dirty = Object.keys(draft).length > 0;
  const isOwner = meta.user(client.header?.userId)?.role === 'owner';

  // The view keeps its own columns: borrowed ones are shown, not written into it.
  const save = useCallback(() => {
    if (!view || !effective) return;
    client.mutate('view.save', { id: view.id, typeId: view.typeId, name: view.name, layout: effective.layout, filter: effective.filter, sort: effective.sort, groupBy: effective.groupBy, gantt: effective.gantt ?? null, summary: effective.summary ?? null, calendar: effective.calendar ?? null, columns: draft.columns ?? view.columns, shared: view.shared },
      [{ kind: 'view', id: view.id, op: 'patch', data: { ...withoutDrill(draft) } }]);
    keep(view.id, {});
    setDraft({});
  }, [view, effective, draft, client, keep]);

  const reset = useCallback(() => { if (view) { keep(view.id, {}); setDraft({}); } }, [view, keep]);

  const me = client.header?.userId ?? null;
  /**
   * A new view from `from` (the working copy by default). It is put into the local schema at once, so the screen opens it
   * instead of falling back to the first view while the server answers (audit 28.10, P0-1). The owner's views are shared,
   * anyone else's are personal.
   */
  const create = useCallback((name: string, from: ViewDef | undefined = effective): Id | null => {
    if (!view || !from) return null;
    const id = uuidv7();
    const shared = isOwner;
    const input = { id, typeId: view.typeId, name, layout: from.layout, filter: from.filter, sort: from.sort, groupBy: from.groupBy, gantt: from.gantt ?? null, summary: from.summary ?? null, calendar: from.calendar ?? null, columns: from.columns, shared };
    const order = Math.max(0, ...meta.views(view.typeId).map((v) => v.order ?? 0)) + 1;
    client.mutate('view.save', input, [{ kind: 'view', id, op: 'put', data: { ...input, kind: 'view', ownerId: shared ? null : me, order, archivedAt: null } }]);
    return id;
  }, [view, effective, client, isOwner, me, meta]);

  /** A new view from the current working copy; the working copy of the old view is cleared. */
  const saveAs = useCallback((name: string): Id | null => {
    const id = create(name);
    if (id && view) { keep(view.id, {}); setDraft({}); }
    return id;
  }, [create, view, keep]);

  /** Writes the saved view with a change (name, sharing), keeping its saved settings, not the working copy. */
  const patch = useCallback((v: ViewDef, change: Partial<Pick<ViewDef, 'name' | 'shared'>>) => {
    const next = { ...v, ...change };
    client.mutate('view.save', { id: v.id, typeId: v.typeId, name: next.name, layout: v.layout, filter: v.filter, sort: v.sort, groupBy: v.groupBy, gantt: v.gantt ?? null, summary: v.summary ?? null, calendar: v.calendar ?? null, columns: v.columns, shared: next.shared },
      [{ kind: 'view', id: v.id, op: 'patch', data: { ...change, ...(change.shared !== undefined ? { ownerId: change.shared ? null : me } : {}) } }]);
  }, [client, me]);

  const remove = useCallback((v: ViewDef) => {
    client.mutate('view.archive', { id: v.id }, [{ kind: 'view', id: v.id, op: 'patch', data: { archivedAt: new Date().toISOString() } }]);
    keep(v.id, {});
  }, [client, keep]);

  /** Who may change a saved view: the owner of the account any, a person their own personal one. */
  const mayEdit = (v: ViewDef) => isOwner || (!v.shared && v.ownerId === me);

  return { view: effective, draft, dirty, drill, back, drilled: draft.drill ?? null, update, save, reset, saveAs, create, patch, remove, mayEdit, isOwner, canSave: view ? mayEdit(view) : false };
}
